import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import tvBroadcast from './tvBroadcastStyles';

/**
 * 駅前写真を16:9に cover 表示し、その上にUIを重ねる
 */
function TVStationOverlayShell({
  imageSrc,
  title,
  titleMeta,
  caption,
  captionLabel,
  compact = false,
  airy = false,
  contentMaxWidth = '100%',
  children,
}) {
  const b = tvBroadcast;
  const gradient = airy
    ? `linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.55) 0%,
        rgba(255, 255, 255, 0.08) 28%,
        rgba(8, 40, 80, 0.02) 52%,
        rgba(13, 74, 143, 0.12) 100%
      )`
    : `linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.78) 0%,
        rgba(255, 255, 255, 0.2) 24%,
        rgba(8, 40, 80, 0.06) 48%,
        rgba(13, 74, 143, 0.22) 100%
      )`;

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 0.5,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${imageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: gradient,
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 1,
            px: compact ? 0.55 : 0.75,
            py: compact ? 0.35 : 0.45,
            bgcolor: airy ? 'rgba(255,255,255,0.78)' : 'rgba(255,255,255,0.88)',
            borderBottom: '3px solid #fff',
            boxShadow: '0 2px 12px rgba(13,74,143,0.15)',
          }}
        >
          <Typography sx={b.title(compact)}>{title}</Typography>
          {titleMeta && (
            <Typography
              sx={{
                fontSize: compact ? '0.58rem' : 'clamp(0.75rem, 1.15vw, 0.98rem)',
                fontWeight: 800,
                color: '#4a6f8f',
                whiteSpace: 'nowrap',
                pb: compact ? 0.1 : 0.35,
              }}
            >
              {titleMeta}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: compact ? 0.5 : 0.85,
            py: compact ? 0.35 : 0.5,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: contentMaxWidth,
              minHeight: 0,
              maxHeight: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {children}
          </Box>
        </Box>

        {caption && (
          <Box
            sx={{
              ...b.ticker(compact),
              ...(airy
                ? {
                    background: 'rgba(21, 101, 184, 0.82)',
                    backdropFilter: 'blur(4px)',
                  }
                : {}),
            }}
          >
            {captionLabel && (
              <Typography
                sx={{
                  fontSize: compact ? '0.72rem' : 'clamp(0.95rem, 1.6vw, 1.25rem)',
                  fontWeight: 900,
                  color: 'rgba(255,255,255,0.92)',
                  textAlign: 'center',
                  mb: 0.15,
                  letterSpacing: '0.04em',
                }}
              >
                {captionLabel}
              </Typography>
            )}
            <Typography sx={b.tickerText(compact)}>{caption}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

TVStationOverlayShell.propTypes = {
  imageSrc: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleMeta: PropTypes.string,
  caption: PropTypes.string,
  captionLabel: PropTypes.string,
  compact: PropTypes.bool,
  airy: PropTypes.bool,
  contentMaxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
};

export default TVStationOverlayShell;
